import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { useAuth } from '../../../auth/AuthProvider';
import { OrganizationIngestChannelsCard } from '../../../components/upload/OrganizationIngestChannelsCard';
import { DeleteOrganizationDialog } from './DeleteOrganizationDialog';
import { InvitePersonForm } from './InvitePersonForm';
import { PendingInvitationsList } from './PendingInvitationsList';
import { canAccessProviderAdmin } from './providerAdminAccess';

const TABS = [
  { id: 'setup', label: 'Setup' },
  { id: 'people', label: 'People' },
  { id: 'digitize', label: 'Digitize' },
];

export function OrganizationDetailPage() {
  const { orgId } = useParams();
  const { session } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('setup');
  const [org, setOrg] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [members, setMembers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [invitesToken, setInvitesToken] = useState(0);
  const [binding, setBinding] = useState(null);
  const [digitizeForm, setDigitizeForm] = useState({
    abdp_e_id: '',
    default_q_id: '',
    status: 'ACTIVE',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const allowed = canAccessProviderAdmin(session);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await providerAdminApi.getOrganization(orgId);
      setOrg(data);
      try {
        // Every org in scope, so the invite form can target any of them.
        const scoped = await providerAdminApi.listOrganizations({ page: 1, page_size: 100 });
        const list = scoped?.items || [];
        setOrganizations(list.some((row) => row.id === data.id) ? list : [data, ...list]);
      } catch {
        setOrganizations([data]);
      }
      try {
        const memberRows = await providerAdminApi.listMembers(orgId);
        setMembers(Array.isArray(memberRows) ? memberRows : []);
      } catch {
        setMembers([]);
      }
      try {
        const bindings = await providerAdminApi.listDigitizeBindings();
        const found = (bindings || []).find((row) => row.organization_id === orgId) || null;
        setBinding(found);
        if (found) {
          setDigitizeForm({
            abdp_e_id: String(found.abdp_e_id),
            default_q_id: found.default_q_id,
            status: found.status,
          });
        }
      } catch {
        setBinding(null);
      }
    } catch (err) {
      setError(err.message || 'Organization not found');
      setOrg(null);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (allowed && orgId) {
      load();
    } else {
      setLoading(false);
    }
  }, [allowed, orgId, load]);

  if (!allowed) {
    return (
      <p className="text-sm text-eorig-muted">
        Provider Admin access is required.
      </p>
    );
  }

  async function handleBinding(event) {
    event.preventDefault();
    setError(null);
    try {
      const saved = await providerAdminApi.upsertDigitizeBinding(orgId, {
        abdp_e_id: Number(digitizeForm.abdp_e_id),
        default_q_id: digitizeForm.default_q_id,
        status: digitizeForm.status,
      });
      setBinding(saved);
    } catch (err) {
      setError(err.message || 'Digitize binding failed');
    }
  }

  if (loading) {
    return <p className="text-sm text-eorig-muted">Loading organization…</p>;
  }

  if (!org) {
    return (
      <div className="space-y-3">
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <p className="text-sm text-eorig-muted">Finish organization setup first.</p>
        <Link to="/provider/admin/organizations" className="text-sm text-[#003A70]">
          Back to organizations
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Link to="/provider/admin/organizations" className="text-sm text-[#003A70]">
          ← Organizations
        </Link>
        <h2 className="mt-2 text-lg font-semibold">{org.name}</h2>
        <p className="text-sm text-eorig-muted">
          {org.code} · {org.org_type} · {org.status}
        </p>
      </div>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2 border-b border-eorig-border">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`px-3 py-2 text-sm ${
              tab === item.id ? 'border-b-2 border-[#003A70] font-semibold' : 'text-eorig-muted'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'setup' ? (
        <div className="rounded-2xl border border-eorig-border bg-white p-4 text-sm">
          <p><span className="font-medium">Name:</span> {org.name}</p>
          <p><span className="font-medium">Code:</span> {org.code}</p>
          <p><span className="font-medium">Type:</span> {org.org_type}</p>
          <p><span className="font-medium">Status:</span> {org.status}</p>
          <p><span className="font-medium">Contact:</span> {org.contact_email || '—'}</p>
          <p className="mt-3 text-eorig-muted">
            Next: invite people, provision SFTP/file-share drop paths, then bind Digitize if this is
            an Originator.
          </p>
          <OrganizationIngestChannelsCard
            org={org}
            loadChannels={providerAdminApi.getOrganizationIngestChannels}
          />
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-eorig-border pt-4">
            <p className="text-eorig-muted">
              Remove this organization. Only possible while it has no packages, programs, user
              accounts or exports.
            </p>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
            >
              Delete organization
            </button>
          </div>
        </div>
      ) : null}

      {tab === 'people' ? (
        <div className="space-y-4">
          <p className="text-sm text-eorig-muted">
            Invite someone as a Provider, Originator, Custodian or Investor user. Defaults to {org.name}.
          </p>
          <InvitePersonForm
            organizations={organizations}
            initialOrgId={org.id}
            onInvited={(targetOrgId) => {
              setInvitesToken((value) => value + 1);
              return targetOrgId === orgId ? load() : undefined;
            }}
          />
          <h3 className="text-sm font-semibold text-eorig-ink">
            Pending invitations to {org.name}
          </h3>
          <PendingInvitationsList
            organizations={[org]}
            refreshToken={invitesToken}
            onChanged={load}
          />
          <h3 className="text-sm font-semibold text-eorig-ink">Members of {org.name}</h3>
          {members.length === 0 ? (
            <p className="text-sm text-eorig-muted">No members yet.</p>
          ) : (
            <ul className="rounded-2xl border border-eorig-border bg-white p-4 text-sm">
              {members.map((member) => (
                <li key={member.membership_id} className="border-b border-eorig-border py-2 last:border-0">
                  {member.full_name || member.email} · {member.role} · {member.status}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {tab === 'digitize' ? (
        <form onSubmit={handleBinding} className="max-w-lg space-y-3 rounded-2xl border border-eorig-border bg-white p-4">
          {!org.ready_for_digitize ? (
            <p className="text-sm text-eorig-muted">
              Digitize binding is for Originator organizations that are not suspended.
            </p>
          ) : null}
          <p className="text-sm text-eorig-muted">
            Bind this org to a VA tenant/queue. Queue catalog stays in VA Admin.
          </p>
          {binding ? (
            <p className="text-sm">Current: e_id {binding.abdp_e_id} / q_id {binding.default_q_id}</p>
          ) : (
            <p className="text-sm text-eorig-muted">No Digitize binding yet.</p>
          )}
          <input
            required
            type="number"
            placeholder="ABDP e_id"
            value={digitizeForm.abdp_e_id}
            onChange={(e) => setDigitizeForm((f) => ({ ...f, abdp_e_id: e.target.value }))}
            className="w-full rounded-xl border border-eorig-border px-3 py-2 text-sm"
          />
          <input
            required
            placeholder="default q_id"
            value={digitizeForm.default_q_id}
            onChange={(e) => setDigitizeForm((f) => ({ ...f, default_q_id: e.target.value }))}
            className="w-full rounded-xl border border-eorig-border px-3 py-2 text-sm"
          />
          <button type="submit" className="rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white">
            Save binding
          </button>
        </form>
      ) : null}
      <DeleteOrganizationDialog
        org={org}
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onDeleted={() => navigate('/provider/admin/organizations')}
      />
    </div>
  );
}
